import { useMemberDetails } from '../../../Component/Context/MemberDetails';
import ContentState from "../../../Component/UI/ContentState";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import SupportChatThread, { SupportMessage } from "../../../Component/Support/SupportChatThread";
import api, { restoreSession } from "../../../Component/axios_Interceptor/api";
import url from "../../../Component/server";
import Layout_user from "../../../Component/UI/Layout_user";
import TopBar from "../../../Component/UI/TopBar";
import LoginModal from "../Sign/Modal/LoginModal";
import SupportAutoReply from "./SupportAutoReply";
import SellerInquiryChat from "./SellerInquiryChat";
import { InquirySelection } from "./InquiryProductPicker";

interface SupportChatPageProps { channel?: "platform" | "seller"; }

export default function SupportChatPage({ channel = "seller" }: SupportChatPageProps) {
  const params = useParams<{ seq?: string; conversationSeq?: string }>();
  const location = useLocation();
  const orderParam = channel === "seller" && params.seq ? new URLSearchParams(location.search).get("orderSeq") : null;
  return <ConversationPage key={`${channel}:${params.seq || ""}:${params.conversationSeq || ""}:${orderParam ?? ""}`}
    inquiryOrderSeq={orderParam === null ? undefined : Number(orderParam)}
    channel={channel} storeSeq={params.seq === undefined ? undefined : Number(params.seq)}
    routeConversationSeq={params.conversationSeq === undefined ? undefined : Number(params.conversationSeq)}
    returnTo={location.pathname + location.search + location.hash} />;
}

function ConversationPage({ channel, storeSeq, routeConversationSeq, returnTo, inquiryOrderSeq }: {
  channel: "platform" | "seller"; storeSeq?: number; routeConversationSeq?: number; returnTo: string; inquiryOrderSeq?: number;
}) {
  const navigate = useNavigate();
  const { ensureDetails } = useMemberDetails();
  const defaultName = channel === "platform" ? "플랫폼 관리자" : "셀러 문의";
  const [conversationSeq, setConversationSeq] = useState<number | null>(null);
  const currentConversation = useRef(conversationSeq);
  currentConversation.current = conversationSeq;
  const [humanReplyConversation, setHumanReplyConversation] = useState<number | null>(null);
  const [messagesReady, setMessagesReady] = useState(false);
  const [targetName, setTargetName] = useState(defaultName);
  const [ready, setReady] = useState(false);
  const [initialSelection, setInitialSelection] = useState<InquirySelection | null>(null);
  const [loginVisible, setLoginVisible] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [error, setError] = useState("");
  const [sellerStoreSeq, setSellerStoreSeq] = useState<number | null>(channel === "seller" && Number.isSafeInteger(storeSeq) && Number(storeSeq) > 0 ? Number(storeSeq) : null);
  const [replyVersion, setReplyVersion] = useState(0);
  const [typeOpen, setTypeOpen] = useState(false);
  const [closed, setClosed] = useState(false);
  const [targetChannel, setTargetChannel] = useState(channel);
  const sequence = useRef(0);
  const controller = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    const request = ++sequence.current;
    setInitialSelection(null); setReady(false); setError(""); setNeedsLogin(false); setLoginVisible(false);
    setTypeOpen(false); setClosed(false); setTargetChannel(channel);
    setConversationSeq(null); setHumanReplyConversation(null); setMessagesReady(false); setTargetName(defaultName);
    try {
      const target = routeConversationSeq ?? (channel === "seller" ? storeSeq : 1);
      if (!Number.isSafeInteger(target) || Number(target) < 1) throw new Error("Invalid inquiry target");
      const token = await restoreSession();
      if (request !== sequence.current) return;
      if (!token) { setNeedsLogin(true); setLoginVisible(true); return; }
      if (inquiryOrderSeq !== undefined) {
        if (!Number.isSafeInteger(inquiryOrderSeq) || inquiryOrderSeq < 1) throw new Error("문의할 주문 정보를 확인해주세요.");
        const orderResponse = await api.get(`${url.Communication_url.support_conversations.replace(/\/conversations\/?$/, "")}/customer-orders`, {
          params: { store_seq: storeSeq, order_seq: inquiryOrderSeq }, signal: abort.signal,
        });
        if (request !== sequence.current) return;
        const order = orderResponse.data?.items?.find((item: { seq: number }) => item.seq === inquiryOrderSeq);
        if (!order || !Array.isArray(order.items) || !order.items.length) throw new Error("이 셀러의 주문 정보를 확인할 수 없습니다.");
        setInitialSelection({ order, detailSeqs: order.items.map((item: { seq: number }) => item.seq) });
      }
      if (routeConversationSeq) { setConversationSeq(routeConversationSeq); return; }
      const response = await api.get(url.Communication_url.support_resolve, {
        params: { channel, ...(channel === "seller" ? { store_seq: storeSeq } : {}) }, signal: abort.signal,
      });
      if (request !== sequence.current) return;
      const data = response.data;
      const existingSeq = data?.conversation === null ? null : Number(data?.conversation?.seq);
      if (typeof data?.target_name !== "string" || !data.target_name.trim()
        || (existingSeq !== null && (!Number.isSafeInteger(existingSeq) || existingSeq < 1))) throw new Error("Invalid conversation");
      setTargetName(data.target_name);
      setConversationSeq(existingSeq);
      setMessagesReady(existingSeq === null);
      setTypeOpen(channel === "seller" && existingSeq === null);
    } catch (err: any) {
      if (request === sequence.current) setError(err?.response?.data?.message || (inquiryOrderSeq !== undefined ? err?.message : undefined) || "상담 정보를 불러오지 못했습니다.");
    } finally { if (request === sequence.current) setReady(true); }
  }, [channel, defaultName, routeConversationSeq, storeSeq, inquiryOrderSeq]);

  useEffect(() => {
    void load();
    return () => { sequence.current += 1; controller.current?.abort(); };
  }, [load]);

  const loadMessages = useCallback(async (signal?: AbortSignal): Promise<SupportMessage[]> => {
    if (!conversationSeq) return [];
    const request = sequence.current;
    const response = await api.get(`${url.Communication_url.support_conversations}/${conversationSeq}/messages`, { signal });
    if (request === sequence.current && !signal?.aborted && currentConversation.current === conversationSeq) {
      const conversation = response.data?.conversation;
      if (Array.isArray(response.data?.messages)) {
        setMessagesReady(true);
        if (response.data.messages.some((message: SupportMessage) => message.sender_role === "staff" && !message.is_auto)) setHumanReplyConversation(conversationSeq);
      }
      setClosed(conversation?.status === "resolved");
      if (conversation?.channel === "seller" || conversation?.channel === "platform") setTargetChannel(conversation.channel);
      if (conversation?.target_name) setTargetName(conversation.target_name);
      if (conversation?.channel === "seller" && Number.isSafeInteger(Number(conversation.store_seq)) && Number(conversation.store_seq) > 0) setSellerStoreSeq(Number(conversation.store_seq));
      else if (conversation?.channel === "platform") setSellerStoreSeq(null);
    }
    return response.data?.messages;
  }, [conversationSeq]);

  const sendMessage = useCallback(async (form: FormData) => {
    if (closed) throw new Error("종료된 상담입니다. 새 문의를 시작해주세요.");
    if (!ready || needsLogin || error) throw new Error("상담 정보를 확인해주세요.");
    const request = sequence.current;
    if (!await ensureDetails('nickname') || request !== sequence.current) throw new Error("상세 정보를 입력한 후 문의를 보내주세요.");
    if (conversationSeq) {
      await api.post(`${url.Communication_url.support_conversations}/${conversationSeq}/messages`, form, { headers: { "Content-Type": "multipart/form-data" } });
      if (request === sequence.current) setTypeOpen(false);
      return;
    }
    form.append("channel", targetChannel);
    if (sellerStoreSeq) form.append("store_seq", String(sellerStoreSeq));
    if (!form.has("category")) form.append("category", "기타");
    const response = await api.post(url.Communication_url.support_conversations, form, { headers: { "Content-Type": "multipart/form-data" } });
    if (request !== sequence.current) return;
    const createdSeq = Number(response.data?.conversation_seq);
    if (!Number.isSafeInteger(createdSeq) || createdSeq < 1) throw new Error("Invalid conversation");
    setConversationSeq(createdSeq);
    setTypeOpen(false);
    if (routeConversationSeq) navigate(`/support/conversations/${createdSeq}`, { replace: true });
  }, [targetChannel, conversationSeq, error, needsLogin, ready, sellerStoreSeq, ensureDetails, closed, routeConversationSeq, navigate]);

  const closedReason = closed ? "종료된 상담입니다. 기존 이력은 보관되며 새 문의를 시작할 수 있습니다." : undefined;
  const newInquiry = closed ? <button type="button" onClick={() => { setConversationSeq(null); setHumanReplyConversation(null); setMessagesReady(true); setClosed(false); setTypeOpen(true); setReplyVersion(value => value + 1); }}>새 문의 시작</button> : undefined;
  const autoResolved = useCallback((seq: number) => {
    setConversationSeq(seq); setClosed(false); setReplyVersion(value => value + 1);
    if (routeConversationSeq && seq !== routeConversationSeq) navigate(`/support/conversations/${seq}`, { replace: true });
  }, [navigate, routeConversationSeq]);

  return <Layout_user isScroll="none" background={channel === "seller" ? "white" : undefined}>
    <Page $seller={channel === "seller"}>
      <ConversationHeader>
        <TopBar title={channel === "seller" ? "셀러 문의" : targetName} color={channel === "seller" ? "white" : undefined} />
        {ready && !error && !needsLogin && sellerStoreSeq && !closed && <TypeButton type="button" aria-expanded={typeOpen} onClick={() => setTypeOpen(value => !value)}>
          채팅 유형<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </TypeButton>}
      </ConversationHeader>
      <LoginModal visible={loginVisible} handleModal={() => setLoginVisible(false)} returnTo={returnTo} />
      {!ready ? <ContentState kind="loading" icon="chat" title="상담 정보를 불러오는 중입니다." />
        : error ? <ContentState kind="error" icon="chat" title={error} description="연결 상태를 확인한 뒤 다시 시도해 주세요." action={{ label: "다시 시도", onClick: () => { void load(); } }} />
        : needsLogin ? <ContentState kind="locked" icon="chat" title="로그인 후 상담할 수 있습니다." description="궁금한 점을 남기고 답변을 확인해 보세요." action={{ label: "로그인하기", onClick: () => setLoginVisible(true) }} />
        : <ConversationContent>
          {sellerStoreSeq ? <SellerInquiryChat initialSelection={initialSelection} storeSeq={sellerStoreSeq} targetName={targetName} conversationSeq={conversationSeq || 0} loadMessages={loadMessages} sendMessage={sendMessage} refreshKey={replyVersion} disabledReason={closedReason} footerAction={newInquiry} showGuide={!closed && (typeOpen || !conversationSeq || (messagesReady && humanReplyConversation !== conversationSeq))}>
            {!closed && <SupportAutoReply key={sellerStoreSeq} initialOrder={initialSelection?.order} storeSeq={sellerStoreSeq} conversationSeq={conversationSeq} open={typeOpen} onOpenChange={setTypeOpen} onResolved={autoResolved} />}
          </SellerInquiryChat> : conversationSeq ? <ThreadArea><SupportChatThread key={conversationSeq} conversationSeq={conversationSeq} mineRole="customer" loadMessages={loadMessages} sendMessage={sendMessage} refreshKey={replyVersion} disabledReason={closedReason} footerAction={newInquiry} /></ThreadArea>
        : <NewConversation>
            <p>메시지나 사진을 보내면 상담이 시작됩니다.</p>
            <SupportChatThread conversationSeq={0} mineRole="customer" loadMessages={loadMessages} sendMessage={sendMessage} />
          </NewConversation>}
        </ConversationContent>}
    </Page>
  </Layout_user>;
}

const Page = styled.main<{ $seller: boolean }>`height:var(--browser-layout-height,100dvh);display:grid;grid-template-rows:auto minmax(0,1fr);color:var(--user-text);background:var(--user-surface);`;
const ConversationHeader = styled.div`position:relative;flex:none;`;
const TypeButton = styled.button`
  position:absolute;right:12px;top:14px;z-index:11;display:inline-flex;align-items:center;justify-content:center;gap:4px;
  min-height:32px;padding:0 8px;border:1px solid var(--user-accent);border-radius:6px;
  background:var(--user-accent-soft);color:var(--user-accent);font:12px var(--font-Pretendard-SemiBold);cursor:pointer;
  &::after{content:"";position:absolute;inset:-6px -4px;}
  svg{flex:none;} &[aria-expanded="true"]{border-color:var(--user-button);background:var(--user-button);color:#fff;}
  &[aria-expanded="true"] svg{transform:rotate(180deg);}
  &:hover{filter:brightness(.95);}&:active{filter:brightness(.9);}
  &:focus-visible{outline:2px solid var(--user-accent);outline-offset:2px;}
`;
const ConversationContent = styled.div`display:flex;flex-direction:column;min-height:0;overflow:hidden;`;
const ThreadArea = styled.div`flex:1;min-height:0;`;
const NewConversation = styled.div`flex:1;min-height:0;display:grid;grid-template-rows:auto minmax(0,1fr);>p{margin:0;padding:12px 16px;background:var(--user-accent-soft);color:var(--user-accent);font-size:13px;text-align:center;}`;
